import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtNumber } from "@/lib/api";
import { downloadXlsx, todayStamp } from "@/lib/xlsx";
import { toast } from "sonner";
import ProductoForm from "@/components/ProductoForm";
import LoteForm from "@/components/LoteForm";
import ImportDialog from "@/components/ImportDialog";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, PencilSimple, Eye, Trash, CircleNotch, MagnifyingGlass, UploadSimple, Package, Stack, DownloadSimple } from "@phosphor-icons/react";
import { useSort, SortTh } from "@/components/sortable";

export default function Productos() {
  const navigate = useNavigate();
  const [productos, setProductos] = useState(null);
  const [search, setSearch] = useState("");
  const [prodFormOpen, setProdFormOpen] = useState(false);
  const [loteFormOpen, setLoteFormOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => {
    api.get("/productos").then((r) => setProductos(r.data));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async () => {
    try {
      await api.delete(`/productos/${toDelete.id}`);
      toast.success("Producto eliminado");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error("No se pudo eliminar");
    }
  };

  const filtered = (productos || []).filter((p) => {
    const s = search.trim().toLowerCase();
    if (!s) return true;
    const matchProducto =
      p.nombre.toLowerCase().includes(s) ||
      p.codigo.toLowerCase().includes(s) ||
      (p.categoria || "").toLowerCase().includes(s) ||
      p.tipo.toLowerCase().includes(s);
    const matchLote = (p.lotes || []).some((l) => l.numero_lote.toLowerCase().includes(s));
    return matchProducto || matchLote;
  });

  const matchedLotes = (p) => {
    const s = search.trim().toLowerCase();
    if (!s) return [];
    return (p.lotes || []).filter((l) => l.numero_lote.toLowerCase().includes(s));
  };

  const { sorted, sortKey, sortDir, toggleSort } = useSort(filtered);

  const exportExcel = () => {
    const rows = [["Código", "Nombre", "Categoría", "Tipo", "Lotes", "Stock total", "Punto reorden", "Reorden propio", "Costo promedio", "Costo embalaje", "Valorización", "Alertas"]];
    filtered.forEach((p) =>
      rows.push([
        p.codigo, p.nombre, p.categoria || "", p.tipo, p.num_lotes, p.stock_total, p.reorder_point,
        p.stock_minimo != null ? "Sí" : "Global", p.costo_promedio, p.costo_embalaje_promedio, p.valor, p.alertas,
      ]));
    downloadXlsx(`productos_${todayStamp()}.xlsx`, [
      { name: "Productos", rows, cols: [{ wch: 12 }, { wch: 26 }, { wch: 16 }, { wch: 12 }, { wch: 8 }, { wch: 12 }, { wch: 14 }, { wch: 13 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 8 }] },
    ]);
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="productos-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-[#0F172A]">Productos</h1>
          <p className="text-[#475569] mt-1">Cada producto agrupa uno o varios lotes de envases.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="export-productos-excel" variant="outline" onClick={exportExcel} disabled={!productos || productos.length === 0} className="h-11 border-[#E2E8F0] font-semibold">
            <DownloadSimple size={18} weight="bold" className="mr-1.5" /> Excel
          </Button>
          <Button data-testid="import-button" variant="outline" onClick={() => setImportOpen(true)} className="h-11 border-[#E2E8F0] font-semibold">
            <UploadSimple size={18} weight="bold" className="mr-1.5" /> Importar
          </Button>
          <Button
            data-testid="new-lote-button"
            variant="outline"
            onClick={() => setLoteFormOpen(true)}
            className="h-11 border-[#E2E8F0] font-semibold"
          >
            <Stack size={18} weight="bold" className="mr-1.5" /> Nuevo lote
          </Button>
          <Button
            data-testid="new-product-button"
            onClick={() => {
              setEditing(null);
              setProdFormOpen(true);
            }}
            className="h-11 bg-[#0F172A] hover:bg-[#1E293B] text-white font-semibold"
          >
            <Plus size={18} weight="bold" className="mr-1.5" /> Nuevo producto
          </Button>
        </div>
      </div>

      <div className="relative sm:max-w-sm">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
        <input
          data-testid="productos-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por producto, código, tipo o número de lote..."
          className="w-full h-10 pl-10 pr-3 rounded-md bg-white border border-[#E2E8F0] text-sm focus:outline-none focus:ring-2 focus:ring-[#0F172A]"
        />
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-md overflow-hidden">
        {productos === null ? (
          <div className="flex items-center justify-center py-20">
            <CircleNotch size={28} className="animate-spin text-[#0F172A]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <Package size={40} weight="duotone" className="mx-auto text-[#94A3B8] mb-3" />
            <p className="text-[#64748B]">No hay productos. Crea uno o importa desde un archivo.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="productos-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-[#64748B] bg-[#F8F9FA]">
                  <SortTh label="Producto" sortKey="nombre" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                  <SortTh label="Categoría" sortKey="categoria" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                  <SortTh label="Tipo" sortKey="tipo" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                  <SortTh label="Lotes" sortKey="num_lotes" activeKey={sortKey} dir={sortDir} onSort={toggleSort} align="right" />
                  <SortTh label="Cant. inicial" sortKey="cantidad_inicial" activeKey={sortKey} dir={sortDir} onSort={toggleSort} align="right" />
                  <SortTh label="Disponible" sortKey="stock_total" activeKey={sortKey} dir={sortDir} onSort={toggleSort} align="right" />
                  <SortTh label="Transac." sortKey="num_transacciones" activeKey={sortKey} dir={sortDir} onSort={toggleSort} align="right" />
                  <SortTh label="Reorden" sortKey="reorder_point" activeKey={sortKey} dir={sortDir} onSort={toggleSort} align="right" />
                  <SortTh label="Costo prom." sortKey="costo_promedio" activeKey={sortKey} dir={sortDir} onSort={toggleSort} align="right" />
                  <SortTh label="Costo embalaje" sortKey="costo_embalaje_promedio" activeKey={sortKey} dir={sortDir} onSort={toggleSort} align="right" />
                  <SortTh label="Valor" sortKey="valor" activeKey={sortKey} dir={sortDir} onSort={toggleSort} align="right" />
                  <SortTh label="Alertas" sortKey="alertas" activeKey={sortKey} dir={sortDir} onSort={toggleSort} align="center" />
                  <th className="px-5 py-3 font-semibold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((p) => (
                  <tr
                    key={p.id}
                    data-testid={`producto-row-${p.id}`}
                    className="border-t border-[#E2E8F0] hover:bg-[#F8F9FA] transition-colors cursor-pointer"
                    onClick={() => navigate(`/productos/${p.id}`)}
                  >
                    <td className="px-5 py-3">
                      <div className="font-semibold text-[#0F172A]">{p.nombre}</div>
                      <div className="text-xs text-[#64748B]">{p.codigo}</div>
                      {matchedLotes(p).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`matched-lotes-${p.id}`}>
                          {matchedLotes(p).map((l) => (
                            <button
                              key={l.id}
                              data-testid={`matched-lote-${l.id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/lotes/${l.id}`);
                              }}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#EEF2FF] text-[#3730A3] text-xs font-semibold hover:bg-[#E0E7FF] transition-colors"
                              title="Ver lote"
                            >
                              <Stack size={12} weight="bold" /> {l.numero_lote}
                            </button>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {p.categoria ? (
                        <span className="text-xs font-medium px-2 py-0.5 rounded bg-[#EFF6FF] text-[#1D4ED8]" data-testid={`categoria-${p.id}`}>{p.categoria}</span>
                      ) : (
                        <span className="text-xs text-[#CBD5E1]">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-[#475569]">{p.tipo}</td>
                    <td className="px-5 py-3 text-right font-semibold text-[#0F172A]">{fmtNumber(p.num_lotes)}</td>
                    <td className="px-5 py-3 text-right text-[#475569]" data-testid={`cant-inicial-${p.id}`}>{fmtNumber(p.cantidad_inicial)}</td>
                    <td className="px-5 py-3 text-right text-[#0F172A]" data-testid={`cant-disponible-${p.id}`}>{fmtNumber(p.stock_total)}</td>
                    <td className="px-5 py-3 text-right text-[#475569]" data-testid={`num-transacciones-${p.id}`}>{fmtNumber(p.num_transacciones)}</td>
                    <td className="px-5 py-3 text-right" data-testid={`reorden-${p.id}`}>
                      <span className={p.stock_total <= p.reorder_point ? "text-[#991B1B] font-semibold" : "text-[#475569]"}>
                        {fmtNumber(p.reorder_point)}
                      </span>
                      {p.stock_minimo == null && <span className="block text-[10px] text-[#94A3B8] leading-none">global</span>}
                    </td>
                    <td className="px-5 py-3 text-right text-[#475569]" data-testid={`costo-promedio-${p.id}`}>{fmtMoney(p.costo_promedio)}</td>
                    <td className="px-5 py-3 text-right text-[#475569]" data-testid={`costo-embalaje-${p.id}`}>{fmtMoney(p.costo_embalaje_promedio)}</td>
                    <td className="px-5 py-3 text-right text-[#475569]">{fmtMoney(p.valor)}</td>
                    <td className="px-5 py-3 text-center">
                      {p.alertas > 0 ? (
                        <span className="inline-flex items-center justify-center min-w-6 h-6 px-2 rounded-full bg-[#FEF2F2] text-[#991B1B] text-xs font-bold">{p.alertas}</span>
                      ) : (
                        <span className="text-xs text-[#94A3B8]">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <button data-testid={`view-producto-${p.id}`} onClick={() => navigate(`/productos/${p.id}`)} className="p-2 rounded-md text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]" title="Ver">
                          <Eye size={18} />
                        </button>
                        <button data-testid={`edit-producto-${p.id}`} onClick={() => { setEditing(p); setProdFormOpen(true); }} className="p-2 rounded-md text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]" title="Editar">
                          <PencilSimple size={18} />
                        </button>
                        <button data-testid={`delete-producto-${p.id}`} onClick={() => setToDelete(p)} className="p-2 rounded-md text-[#475569] hover:bg-[#FEF2F2] hover:text-[#991B1B]" title="Eliminar">
                          <Trash size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ProductoForm open={prodFormOpen} onOpenChange={setProdFormOpen} producto={editing} onSaved={load} />
      <LoteForm open={loteFormOpen} onOpenChange={setLoteFormOpen} onSaved={load} />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={load} />

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-producto-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este producto?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará "{toDelete?.nombre}" junto con sus {toDelete?.num_lotes} lote(s) y movimientos. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-producto-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-producto-confirm" onClick={handleDelete} className="bg-[#DC2626] hover:bg-[#991B1B]">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
